import { memo, useCallback, useMemo, useState } from "react";
import Dropdown from "ui/components/Dropdown/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { CardSelect } from "ui/components/CardSelect";
import { ListingContact } from "services/api/settings/listings/types";
import { MarketplaceContact } from "../../../types";
import { CONTACT_MODE_OPTIONS } from "../../../consts";
import { SectionCard } from "../primitives";

type ContactMode = "settings" | "manual";

interface Props {
  values: MarketplaceContact;
  onChange: (patch: Partial<MarketplaceContact>) => void;
  /** Djelatnici iz zadanih postavki oglasa; korak ih učita prije rendera. */
  settingsContacts: ListingContact[];
  /** Poruka kad korak ne prolazi jer kontakt nije odabran. */
  error?: string;
}

// Kontakt na oglasu. Iz postavki se bira jedan djelatnik zadan u postavkama oglasa;
// ručno se bira kontakt iz imenika (osoba ili tvrtka). Dva izvora se isključuju — koji
// je god odabran, drugi se prazni, pa payload nikad ne nosi oba.
//
// Način se ne sprema zasebno: popunjen `contactId` znači ručni odabir.
const ContactSection: React.FC<Props> = ({ values, onChange, settingsContacts, error }) => {
  const [mode, setMode] = useState<ContactMode>(
    values.contactId ? "manual" : "settings"
  );

  const settingsOptions: DropdownOption[] = useMemo(
    () =>
      settingsContacts.map((contact) => ({
        value: contact.userId,
        text: contact.fullName,
      })),
    [settingsContacts]
  );

  const handleSettingsContact = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onChange({
        contactUserIds: item.value ? [item.value] : [],
        contactId: "",
      });
    },
    [onChange]
  );

  const handleManualContact = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onChange({ contactId: item.value ?? "", contactUserIds: [] });
    },
    [onChange]
  );

  const handleModeChange = useCallback(
    (next: string) => {
      if (next === "settings") {
        setMode("settings");
        // Na oglasu stoji jedan kontakt, pa se uzima prvi iz postavki — korisnik
        // ga može zamijeniti drugim iz iste liste.
        onChange({
          contactUserIds: settingsContacts.slice(0, 1).map((c) => c.userId),
          contactId: "",
        });
        return;
      }

      setMode("manual");
      // Djelatnik se miče čim se prelazi na imenik; kontakt se tek bira.
      onChange({ contactUserIds: [], contactId: "" });
    },
    [onChange, settingsContacts]
  );

  return (
    <SectionCard
      title="Kontakt"
      subtitle="Kontakt se preuzima iz postavki oglasa ili se bira za ovaj oglas."
    >
      <div className="mb-4">
        <CardSelect
          options={CONTACT_MODE_OPTIONS}
          value={mode}
          onChange={handleModeChange}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          {mode === "settings" ? (
            <Dropdown
              name="contactUserId"
              label="Kontakt osoba"
              placeholder="Odaberite kontakt osobu"
              options={settingsOptions}
              value={values.contactUserIds[0] ?? ""}
              onChange={handleSettingsContact}
              error={error}
              required
              width="full"
            />
          ) : (
            <ContactDropdown
              name="contactId"
              label="Kontakt"
              placeholder="Odaberite kontakt iz imenika"
              value={values.contactId}
              onChange={handleManualContact}
              error={error}
              required
              width="full"
            />
          )}
        </div>
      </div>
    </SectionCard>
  );
};

export default memo(ContactSection);
